import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchPaymentMethods,
  fetchPaymentMethodById,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
  reorderPaymentMethods,
  uploadPaymentMethodImage,
  deletePaymentMethodImage,
  fetchPaymentMethodImageUrls,
  PAYMENT_METHOD_IMAGE_URL_TTL_MS,
} from '@/lib/api'
import type {
  PaymentMethod,
  PaymentMethodInsert,
  PaymentMethodUpdate,
} from '@/lib/api'
import { mutationErrorHandler } from '@/lib/utils'

export type { PaymentMethod } from '@/lib/api'

export function usePaymentMethods() {
  return useQuery<PaymentMethod[]>({
    queryKey: ['payment-methods'],
    queryFn: fetchPaymentMethods,
  })
}

export function usePaymentMethod(id: string | undefined) {
  return useQuery<PaymentMethod | null>({
    queryKey: ['payment-methods', id],
    queryFn: () => fetchPaymentMethodById(id as string),
    enabled: !!id,
  })
}

export function useCreatePaymentMethod() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PaymentMethodInsert) => createPaymentMethod(input),
    onSuccess: () => {
      toast.success('支払い方法を追加しました')
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
    },
    onError: mutationErrorHandler('保存に失敗しました'),
  })
}

export function useUpdatePaymentMethod() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PaymentMethodUpdate }) =>
      updatePaymentMethod(id, data),
    onSuccess: () => {
      toast.success('支払い方法を更新しました')
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
    },
    onError: mutationErrorHandler('更新に失敗しました'),
  })
}

// 並び替えは React Query キャッシュには触らずサーバ書き込みのみ行う。
// ページ側でローカル state を主データソースにして drop と同レンダーで描画反映する
// (gardenapp の StatusManager と同じ流儀)。キャッシュを setQueryData で動かすと
// 下流の useMemo / SortableContext items が再生成されて drop 時に「跳ね」が出る。
export function useReorderPaymentMethods() {
  return useMutation({
    mutationFn: (orders: { id: string; sort_order: number }[]) => reorderPaymentMethods(orders),
    onError: mutationErrorHandler('並び替えに失敗しました'),
  })
}

export function useDeletePaymentMethod() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deletePaymentMethod(id),
    onSuccess: () => {
      toast.success('支払い方法を削除しました')
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      queryClient.invalidateQueries({ queryKey: ['recurring'] })
    },
    onError: mutationErrorHandler('削除に失敗しました'),
  })
}

export function useUploadPaymentMethodImage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      uploadPaymentMethodImage(id, file),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      queryClient.invalidateQueries({ queryKey: ['payment-method-images'] })
      queryClient.invalidateQueries({ queryKey: ['payment-methods', id] })
    },
    onError: mutationErrorHandler('画像のアップロードに失敗しました'),
  })
}

export function useDeletePaymentMethodImage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deletePaymentMethodImage(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      queryClient.invalidateQueries({ queryKey: ['payment-method-images'] })
      queryClient.invalidateQueries({ queryKey: ['payment-methods', id] })
    },
    onError: mutationErrorHandler('画像の削除に失敗しました'),
  })
}

export function usePaymentMethodImageUrls(sources: PaymentMethod[] | undefined) {
  const paths = useMemo(
    () => (sources ?? []).map((s) => s.image_path).filter((p): p is string => !!p),
    [sources],
  )
  const key = useMemo(() => [...paths].sort().join('|'), [paths])
  return useQuery<Record<string, string>>({
    queryKey: ['payment-method-images', key],
    queryFn: () => fetchPaymentMethodImageUrls(paths),
    enabled: paths.length > 0,
    staleTime: PAYMENT_METHOD_IMAGE_URL_TTL_MS / 2,
  })
}
